import { isTestMode } from "lib/environment";
import BaseService from "./BaseService";
import { soundService, type Sound } from "./SoundService";
import Debug from "debug";

const debug = Debug("mixitone:MessagesService");

const MESSAGE_TIMEOUT = 3000;

export type MessageType = "error" | "success" | "warning" | "registered";

export interface AppMessage {
  id: number;
  body: React.ReactNode;
  type: MessageType;
  fading?: boolean;
  sound?: Sound;
}

interface State {
  messages: AppMessage[];
}

let messageId = 0;

export class MessagesService extends BaseService<State> {
  private static instance: MessagesService | null = null;

  get initialState(): State {
    return {
      messages: [],
    };
  }

  /**
   * Get the singleton instance of MessagesService
   */
  static getInstance(): MessagesService {
    if (!MessagesService.instance) {
      MessagesService.instance = new MessagesService();
    }
    return MessagesService.instance;
  }

  /**
   * Clear the singleton instance (useful for testing)
   */
  static clearInstance(): void {
    MessagesService.instance = null;
  }

  /**
   * Add a message to the message queue
   * @param body The message content
   * @param type The message type
   * @param sound Optional sound to play
   */
  addMessage(body: React.ReactNode, type: MessageType, sound?: Sound) {
    const id = messageId++;
    debug("Adding message", { id, body, type, sound });
    this.state.messages.push({ id, body, type, sound });

    if (sound) {
      soundService.play(sound);
    }

    if (this.state.messages.length > 3) {
      // Fade all but the last 3 messages
      this.state.messages.slice(0, -3).forEach((message) => {
        this.acknowledgeMessage(message.id);
      });
    }

    // Auto-fade all messages including registered ones
    setTimeout(() => {
      this.acknowledgeMessage(id);
    }, MESSAGE_TIMEOUT);
  }

  /**
   * Clear all messages
   */
  clearMessages() {
    debug("Clearing all messages");
    this.state.messages = [];
  }

  /**
   * Acknowledge a message (start fading animation)
   * @param id The message ID
   */
  acknowledgeMessage(id: number) {
    debug("Acknowledging message", id);
    const message = this.state.messages.find((message) => message.id === id);
    if (message) message.fading = true;
  }

  /**
   * Delete a message from the queue
   * @param id The message ID
   */
  deleteMessage(id: number) {
    debug("Deleting message", id);
    const index = this.state.messages.findIndex((message) => message.id === id);
    if (index === -1) return;
    this.state.messages.splice(index, 1);
  }
}

// Create a singleton instance for global access
export const messagesService = MessagesService.getInstance();
export default messagesService;

if (isTestMode()) {
  // @ts-expect-error - This is a test mode only
  window.messagesService = messagesService;
}
